import { FC, useEffect, useState } from "react";
import {
	collectStats,
	combineBatStats,
	combineBowlStats,
	combineFieldStats,
	getSeasons,
	getTeamGradeIds
} from "../cricket-api/combined-api";
import {
	PlayerBatStats,
	PlayerBowlStats,
	PlayerFieldStats,
	Season,
	type Team
} from "../schema/schema";
import { Loader, Select } from "@mantine/core";
import BatStatsTable from "./table/BatStatsTable";
import BowlStatsTable from "./table/BowlStatsTable";
import FieldStatsTable from "./table/FieldStatsTable";
import { ChartBarIcon, FireIcon } from "@heroicons/react/24/outline";

type StatTab = "Batting" | "Bowling" | "Fielding";

const selectClassNames = {
	input: "bg-slate-900/70 border-slate-600 text-slate-100",
	label: "text-slate-300",
	dropdown: "bg-white border border-slate-300 text-slate-900",
	option:
		"text-slate-900 data-[hovered]:bg-slate-100 data-[checked]:bg-emerald-100 data-[checked]:text-slate-900"
};

const TeamStats: FC<{ orgId: string }> = ({ orgId }) => {
	const [seasons, setSeasons] = useState<Season[]>([]);
	const [selectedSeason, setSelectedSeason] = useState<string>("");
	const [grades, setGrades] = useState<Team[]>([]);
	const [selectedGrade, setSelectedGrade] = useState<string>("");
	const [selectedStat, setSelectedStat] = useState<StatTab>("Batting");

	const [batStats, setBatStats] = useState<PlayerBatStats[]>([]);
	const [bowlStats, setBowlStats] = useState<PlayerBowlStats[]>([]);
	const [fieldStats, setFieldStats] = useState<PlayerFieldStats[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string>("");

	useEffect(() => {
		(async () => {
			setLoading(true);
			setError("");
			try {
				const seasonsResponse = await getSeasons(orgId);
				setSeasons(seasonsResponse);
				setSelectedSeason(
					seasonsResponse.find(season => season.IsCurrentSeason)?.Id || seasonsResponse[0]?.Id || ""
				);
			} catch {
				setError("Could not load seasons for this club.");
			} finally {
				setLoading(false);
			}
		})();
	}, [orgId]);

	useEffect(() => {
		if (!selectedSeason) {
			return;
		}

		(async () => {
			setLoading(true);
			setError("");
			try {
				const teams = await getTeamGradeIds(orgId, selectedSeason);
				setGrades(teams);

				const nextGrade = selectedGrade || "all";
				if (!selectedGrade) {
					setSelectedGrade("all");
				}

				const teamStats = await collectStats(teams);

				if (nextGrade !== "all") {
					const selectedTeam = teamStats.find(team => team.teamName === nextGrade);
					setBatStats(selectedTeam?.batStats || []);
					setBowlStats(selectedTeam?.bowlStats || []);
					setFieldStats(selectedTeam?.fieldStats || []);
				} else {
					setBatStats(combineBatStats(teamStats));
					setBowlStats(combineBowlStats(teamStats));
					setFieldStats(combineFieldStats(teamStats));
				}
			} catch {
				setError("Could not load player statistics. Please try another season or grade.");
			} finally {
				setLoading(false);
			}
		})();
	}, [selectedSeason, selectedGrade, orgId]);

	if (loading) {
		return (
			<div className="min-h-[380px] flex items-center justify-center">
				<div className="flex flex-col items-center gap-3 text-slate-300">
					<Loader color="teal" size="lg" />
					<p>Loading team statistics...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return <div className="p-6 text-red-300">{error}</div>;
	}

	return (
		<div className="p-3 sm:p-5 md:p-7">
			<div className="w-full">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4">
					<div>
						<Select
							label="Season"
							variant="filled"
							placeholder="Season"
							data={seasons.map(season => ({ value: season.Id, label: season.Name }))}
							value={selectedSeason}
							onChange={season => {
								setSelectedSeason(season || "");
								setSelectedGrade("all");
							}}
							allowDeselect={false}
							classNames={selectClassNames}
						/>
					</div>

					<div>
						<Select
							label="Grade"
							variant="filled"
							placeholder="Grade"
							data={[
								{
									value: "all",
									label: "All"
								},
								...grades.map(grade => ({
									value: grade.Id,
									label: `${grade.Grade.Name} (${grade.Name})`
								}))
							]}
							value={selectedGrade}
							onChange={grade => {
								setSelectedGrade(grade || "");
							}}
							allowDeselect={false}
							classNames={selectClassNames}
						/>
					</div>

					<div>
						<Select
							label="Stats"
							variant="filled"
							placeholder="Stats"
							data={["Batting", "Bowling", "Fielding"]}
							value={selectedStat}
							onChange={stat => {
								setSelectedStat((stat as StatTab) || "Batting");
							}}
							allowDeselect={false}
							leftSection={
								selectedStat === "Batting" ? (
									<FireIcon className="h-4 w-4" />
								) : (
									<ChartBarIcon className="h-4 w-4" />
								)
							}
							classNames={selectClassNames}
						/>
					</div>
				</div>

				{batStats && selectedStat === "Batting" && <BatStatsTable stats={batStats} />}
				{bowlStats && selectedStat === "Bowling" && <BowlStatsTable stats={bowlStats} />}
				{fieldStats && selectedStat === "Fielding" && <FieldStatsTable stats={fieldStats} />}
			</div>
		</div>
	);
};

export default TeamStats;
